import type { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import type { HdMandalaGateGlyph, HdMandalaQuarterTexts, HdMandalaZodiacNames } from '../mandala/mandala-wheel.component';
import type {
  HdMandalaQuarterColorVariant,
  HdMandalaZodiacRingVariant,
  MandalaGeometryConfig,
} from '../mandala/mandala.constants';
import type { HdChartBasics } from '../bodygraph/basic-properties/hd-chart-properties.types';
import { formatHdProfileValue } from '../bodygraph/basic-properties/hd-chart-properties.util';
import { HD_BASIC_PROPERTY_TILE_ICONS, HdBasicPropertyKey } from '../bodygraph/basic-properties/hd-chart-properties.constants';

/** Eén tegel in de kop van de print: icoon (zoals de tegel in het Basis-paneel), label en waarde. */
export interface HdChartPrintBasic {
  readonly key: HdBasicPropertyKey;
  readonly icon: string;
  readonly label: string;
  readonly value: string;
}

/** Incarnatie- en levenskruis, al opgemaakt zoals in het Basis-paneel. */
export interface HdChartPrintCrosses {
  readonly incarnationCross: string;
  readonly lifeCross: string;
}

/** Het wiel van de Mandala-print: alle ringen, opgebouwd zoals de mandala-weergave in de app. */
export interface HdChartPrintMandala {
  readonly geometry: MandalaGeometryConfig;
  /** Wiel-schaal van `buildMandalaWheelLayout`: de chart in het midden schaalt mee, zoals in de app. */
  readonly scale: number;
  readonly gateGlyphs: readonly HdMandalaGateGlyph[];
  readonly quarterTexts: HdMandalaQuarterTexts;
  readonly zodiacSignNames: HdMandalaZodiacNames;
  readonly quarterColorVariant: HdMandalaQuarterColorVariant;
  readonly zodiacRingVariant: HdMandalaZodiacRingVariant;
  readonly sliceCenterFill: boolean;
}

/**
 * Voornaam voor de print: het eerste woord van de chartnaam (een chart heeft één naamveld).
 * "Marie-Louise Peeters" → "Marie-Louise", "Anne Marie Peeters" → "Anne". Wordt later instelbaar.
 */
export function hdPrintFirstName(chartName: string): string {
  return chartName.trim().split(/\s+/)[0] ?? '';
}

/**
 * De kop van de print als rijen tegels, zoals het Basis-paneel ze toont. `t` vertaalt
 * `humanDesign.properties.<key>` in de contenttaal.
 * - Zonder kruisen (Basis): één rij met type, strategie, autoriteit, profiel en definitie.
 * - Met kruisen (Regulier, Mandala): type t/m profiel, en een tweede rij met de definitie naast
 *   het incarnatie- en levenskruis (de lange waarden). Een kruis zonder waarde valt weg.
 */
export function buildHdChartPrintBasics(
  basics: HdChartBasics,
  t: (key: string) => string,
  crosses?: HdChartPrintCrosses,
): HdChartPrintBasic[][] {
  const tile = (key: HdBasicPropertyKey, value: string): HdChartPrintBasic =>
    ({ key, icon: HD_BASIC_PROPERTY_TILE_ICONS[key], label: t(key), value });
  const type = [
    basics.type.pure ? t('purePrefix') : '',
    basics.type.modifier ? t(`typeValues.${basics.type.modifier}`) : '',
    t(`typeValues.${basics.type.base}`),
  ]
    .filter(part => part !== '')
    .join(' ');

  const main = [
    tile('type', type),
    tile('strategy', t(`strategyValues.${basics.strategyKey}`)),
    tile('authority', t(`authorityValues.${basics.authorityKey}`)),
    tile('profile', formatHdProfileValue(basics.profile, t)),
  ];
  const definition = tile('definition', t(`definitionValues.${basics.definitionKey}`));
  if (!crosses) return [[...main, definition]];

  const crossTiles = [tile('incarnationCross', crosses.incarnationCross), tile('lifeCross', crosses.lifeCross)]
    .filter(cross => cross.value !== '');
  return [main, [definition, ...crossTiles]];
}

/**
 * Printbaar = een berekende chart met planeetactivaties aan beide kanten. Bij cycle-, transit- en
 * composite-charts is het bovenste niveau de natal-chart (van persoon 1): die wordt geprint.
 */
export function isHdChartPrintable(preset: HdBodygraphChartPreset | null | undefined): preset is HdBodygraphChartPreset {
  const activations = preset?.planetaryActivations ?? [];
  return (
    activations.some(activation => activation.side === 'design') &&
    activations.some(activation => activation.side === 'personality')
  );
}
